import type { Receipt } from '../types';
import type { RouteMode } from '../types';
import { findFirstMajorIataPair, isMajorCommercialIata } from './airportIataMajor';

/** One transport leg extracted from a boarding pass or agent itinerary PDF */
export interface ExtractedTransportLeg {
  mode: RouteMode;
  origin: string;
  destination: string;
  depart_at: string | null;
  arrive_at: string | null;
  flight_number: string | null;
  carrier: string | null;
}

const MONTHS: Record<string, string> = {
  jan: '01',
  feb: '02',
  mar: '03',
  apr: '04',
  may: '05',
  jun: '06',
  jul: '07',
  aug: '08',
  sep: '09',
  oct: '10',
  nov: '11',
  dec: '12',
};

function pad2(n: string): string {
  return n.length === 1 ? `0${n}` : n;
}

/** Build local ISO-like string for datetime-local / Date parsing */
function combineDateAndTime(
  y: string,
  m: string,
  d: string,
  time: string | null
): string | null {
  if (!time || !/^\d{1,2}:\d{2}$/.test(time.trim())) return `${y}-${m}-${d}T12:00:00`;
  const [hh, mm] = time.trim().split(':');
  return `${y}-${m}-${d}T${pad2(hh)}:${pad2(mm)}:00`;
}

function parseWeekdayDate(m: RegExpMatchArray): { y: string; m: string; d: string } | null {
  const d = m[2];
  const mon = MONTHS[m[3].toLowerCase().slice(0, 3)];
  const y = m[4];
  if (!mon || !d || !y) return null;
  return { y, m: mon, d: pad2(d) };
}

function parseDmyDate(m: RegExpMatchArray): { y: string; m: string; d: string } | null {
  const d = m[1];
  const mon = MONTHS[m[2].toLowerCase().slice(0, 3)];
  const y = m[3];
  if (!mon || !d || !y) return null;
  return { y, m: mon, d: pad2(d) };
}

/** Strip OCR junk / merged footer text from endpoint fields */
const ENDPOINT_JUNK =
  /fare\s+information|ticket\s*price|carbon|emissions|route\s+description|important\s+information|products|platforms|booking\s+reference|e-ticket|travel\s+arrangements|page\s+\d+\s+of|american\s+express|global\s+business\s+travel|class:|status:|estimated|aircraft|meal\s+service|check\s*in|baggage|number\s+of\s+stops/i;

function cleanEndpointValue(raw: string): string | null {
  if (!raw) return null;
  let s = raw.replace(/\s+/g, ' ').trim();
  const ji = s.search(ENDPOINT_JUNK);
  if (ji >= 0) s = s.slice(0, ji).trim();
  s = s.replace(/\s*[·•]\s*$/g, '').trim();
  const pipe = s.indexOf('|');
  if (pipe > 8 && pipe < s.length - 3) s = s.slice(0, pipe).trim();
  if (s.length > 90) s = `${s.slice(0, 87).trim()}…`;
  if (!isPlausibleEndpoint(s)) return cleanEndpointLoose(s);
  return s;
}

/** When strict rules fail — still block obvious OCR soup */
function cleanEndpointLoose(s: string): string | null {
  const t = s.trim();
  if (t.length < 2 || t.length > 90) return null;
  if (ENDPOINT_JUNK.test(t)) return null;
  if ((t.match(/\b(products|platforms)\b/gi) || []).length >= 2) return null;
  if ((t.match(/[A-Za-zÀ-ÿ]/g) || []).length < 2) return null;
  return t;
}

function isPlausibleEndpoint(s: string): boolean {
  const t = s.trim();
  if (t.length < 2 || t.length > 90) return false;
  if (ENDPOINT_JUNK.test(t)) return false;
  if (/\b(and|or)\b/i.test(t) && t.length > 48) return false;
  if ((t.match(/\b(products|platforms|fare|ticket|information)\b/gi) || []).length >= 3) return false;
  const letters = (t.match(/[A-Za-zÀ-ÿ]/g) || []).length;
  if (letters < 2) return false;
  if (/^[A-Z]{3}$/.test(t)) return true;
  if (/\b[A-Z]{3}\b/.test(t)) return true;
  if (/[A-Za-zÀ-ÿ]{2,}/.test(t)) return true;
  return false;
}

/**
 * Read "Origin: …" up to the next known label (PDF text is often one long line).
 */
function extractLabeledField(section: string, field: 'Origin' | 'Destination'): string | null {
  const stopAfterOrigin =
    'Destination|Flight|Departing|Arriving|Airline|Fare|Carbon|Important|Class|Status|Estimated|Aircraft|Meal|Number|Stops|Check-in|Baggage|Route|Ticket|Booking|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday|Travel Details|Flight Information';
  const stopAfterDest =
    'Origin|Flight|Departing|Arriving|Airline|Fare|Carbon|Important|Class|Status|Estimated|Aircraft|Meal|Number|Stops|Check-in|Baggage|Route|Ticket|Booking|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday|Travel Details|Flight Information';
  const next = field === 'Origin' ? stopAfterOrigin : stopAfterDest;
  const re = new RegExp(
    `${field}\\s*:\\s*([\\s\\S]*?)(?=\\s*(?:${next})\\s*:|$)`,
    'i'
  );
  let m = section.match(re);
  if (!m) {
    const loose = new RegExp(
      `${field}\\s*:\\s*([^\\n]+?)\\s*(?=(?:${field === 'Origin' ? 'Destination' : 'Origin'})\\s*:|Flight\\s*:|Departing\\s*:|$)`,
      'i'
    );
    m = section.match(loose);
  }
  if (!m) return null;
  let chunk = m[1].trim();
  if (chunk.length > 120) chunk = chunk.slice(0, 120);
  return cleanEndpointValue(chunk);
}

function extractOriginDestinationDirect(section: string): { origin: string; destination: string } | null {
  const pair = section.match(
    /Origin\s*:\s*([\s\S]{0,220}?)\s*Destination\s*:\s*([\s\S]{0,220}?)(?=\s*(?:Flight|Departing|Arriving|Airline|Fare|Class|Carbon|Important|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s*:|$)/i
  );
  if (!pair) return null;
  const o = cleanEndpointValue(pair[1].replace(/\s+/g, ' ').trim());
  const d = cleanEndpointValue(pair[2].replace(/\s+/g, ' ').trim());
  if (o && d) return { origin: o, destination: d };
  return null;
}

function extractFlightNumber(section: string): string | null {
  const m =
    section.match(/Flight\s*:\s*([A-Za-z]{2}\s*\d{3,4})/i) ||
    section.match(/Flight\s*:\s*([A-Z]{2}\d{3,4})/i);
  if (!m) return null;
  return m[1].replace(/\s+/g, '').toUpperCase();
}

function extractTimeAfterLabel(section: string, label: 'Departing' | 'Arriving'): string | null {
  const re = new RegExp(`${label}\\s*:\\s*([^\\n]+)`, 'i');
  const m = section.match(re);
  if (!m) return null;
  const t = m[1].match(/(\d{1,2}:\d{2})/);
  return t ? t[1] : null;
}

function extractAirline(section: string): string | null {
  const m = section.match(/Airline\s*:\s*([^\n]+)/i);
  if (!m) return null;
  const s = m[1].trim().split(/[|·]/)[0].trim();
  return s.length > 1 && s.length < 48 ? s : null;
}

/** Reject bogus IATA–IATA pairs from OCR (e.g. name fragment + real code) when both look like 3-letter codes */
function legEndpointsPlausibleIata(origin: string, destination: string): boolean {
  const o = origin.trim();
  const d = destination.trim();
  if (/^[A-Z]{3}$/i.test(o) && /^[A-Z]{3}$/i.test(d)) {
    return isMajorCommercialIata(o) && isMajorCommercialIata(d);
  }
  return true;
}

/** Agent / Amex GBT style: split on weekday date lines, then bounded Origin/Destination fields */
function extractAgentStyleLegs(text: string): ExtractedTransportLeg[] {
  const legs: ExtractedTransportLeg[] = [];
  const t = text.replace(/\r/g, '\n');
  const sections = t
    .split(
      /(?=(?:Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s+\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4})/i
    )
    .map((s) => s.trim())
    .filter(Boolean);

  const parseSection = (section: string) => {
    const head = section.match(
      /(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s+(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{4})/i
    );
    const currentDate = head ? parseWeekdayDate(head) : null;
    if (!currentDate) return;

    let origin = extractLabeledField(section, 'Origin');
    let destination = extractLabeledField(section, 'Destination');
    if (!origin || !destination) {
      const direct = extractOriginDestinationDirect(section);
      if (direct) {
        origin = direct.origin;
        destination = direct.destination;
      }
    }
    if (!origin || !destination) {
      const rd = section.match(/Route\s+Description:\s*([^\n]+)/i);
      if (rd) {
        const parts = rd[1]
          .split('/')
          .map((p) => p.trim())
          .filter(Boolean);
        if (parts.length >= 2) {
          origin = cleanEndpointValue(parts[0]);
          destination = cleanEndpointValue(parts[1]);
        }
      }
    }
    if (!origin || !destination) return;
    if (!legEndpointsPlausibleIata(origin, destination)) return;

    const flightNum = extractFlightNumber(section);
    const depTime = extractTimeAfterLabel(section, 'Departing');
    const arrTime = extractTimeAfterLabel(section, 'Arriving');
    const carrier = extractAirline(section);
    const { y, m: mo, d } = currentDate;

    legs.push({
      mode: 'flight',
      origin,
      destination,
      depart_at: combineDateAndTime(y, mo, d, depTime),
      arrive_at: arrTime ? combineDateAndTime(y, mo, d, arrTime) : null,
      flight_number: flightNum,
      carrier,
    });
  };

  if (sections.length > 0) {
    for (const s of sections) parseSection(s);
  } else {
    parseSection(t);
  }

  if (legs.length === 0) {
    const head = t.match(
      /(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s+(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{4})/i
    );
    const dt = head ? parseWeekdayDate(head) : null;
    const direct = extractOriginDestinationDirect(t);
    if (dt && direct && legEndpointsPlausibleIata(direct.origin, direct.destination)) {
      const { y, m: mo, d } = dt;
      legs.push({
        mode: 'flight',
        origin: direct.origin,
        destination: direct.destination,
        depart_at: combineDateAndTime(y, mo, d, extractTimeAfterLabel(t, 'Departing')),
        arrive_at: (() => {
          const arr = extractTimeAfterLabel(t, 'Arriving');
          return arr ? combineDateAndTime(y, mo, d, arr) : null;
        })(),
        flight_number: extractFlightNumber(t),
        carrier: extractAirline(t),
      });
    }
  }

  return legs.filter(
    (l) =>
      Boolean(l.origin && l.destination) && legEndpointsPlausibleIata(l.origin, l.destination)
  );
}

/** Boarding pass style: IATA codes, flight number, one date, departure time */
function extractBoardingPassLeg(text: string): ExtractedTransportLeg | null {
  const flat = text.replace(/\s+/g, ' ');
  const low = flat.toLowerCase();

  const flightM = flat.match(/\b([A-Z]{2})\s*(\d{3,4})\b/);
  const flight_number = flightM ? `${flightM[1]}${flightM[2]}` : null;

  const dateM = flat.match(
    /\b(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{4})\b/i
  );
  let y = '';
  let mo = '';
  let d = '';
  if (dateM) {
    const dm = parseDmyDate(dateM as unknown as RegExpMatchArray);
    if (dm) {
      y = dm.y;
      mo = dm.m;
      d = dm.d;
    }
  }
  if (!y) {
    const iso = flat.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
    if (iso) {
      y = iso[1];
      mo = iso[2];
      d = iso[3];
    }
  }
  if (!y) return null;

  const timeMatches: string[] = [];
  const timeRe = /\b(\d{1,2}):(\d{2})\b/g;
  let tm: RegExpExecArray | null;
  while ((tm = timeRe.exec(flat)) !== null) {
    timeMatches.push(`${tm[1]}:${tm[2]}`);
  }
  let depTime: string | null = null;
  if (timeMatches.length) {
    const depIdx = low.search(/depart|boarding|gate opens|std\b/);
    if (depIdx >= 0) {
      const slice = flat.slice(Math.max(0, depIdx - 40), depIdx + 80);
      const near = slice.match(/\b(\d{1,2}:\d{2})\b/);
      depTime = near ? near[1] : timeMatches[0];
    } else {
      depTime = timeMatches[0];
    }
  }

  /** Prefer known hub codes in order — avoids matching name fragments (e.g. AKI) before HEL */
  const majorPair = findFirstMajorIataPair(text);
  let origin = '';
  let destination = '';
  if (majorPair) {
    origin = majorPair.origin;
    destination = majorPair.destination;
  }

  if (!origin || !destination) {
    const headText = text.slice(0, 3500);
    const labeled = headText.match(
      /\b([A-Z]{3})\s*[-–]\s*([A-Za-zÀ-ÿ\s]{3,45}?)\s*[-–\s]{0,4}\b([A-Z]{3})\s*[-–]\s*([A-Za-zÀ-ÿ\s]{3,45})/i
    );
    if (labeled) {
      const codeO = labeled[1].toUpperCase();
      const codeD = labeled[3].toUpperCase();
      if (isMajorCommercialIata(codeO) && isMajorCommercialIata(codeD)) {
        const o = cleanEndpointValue(`${codeO} — ${labeled[2].trim()}`);
        const d = cleanEndpointValue(`${codeD} — ${labeled[4].trim()}`);
        if (o && d) {
          origin = o;
          destination = d;
        }
      }
    }
  }

  if (!origin || !destination) return null;

  return {
    mode: 'flight',
    origin,
    destination,
    depart_at: combineDateAndTime(y, mo, d, depTime),
    arrive_at: null,
    flight_number,
    carrier: /finnair/i.test(flat) ? 'Finnair' : null,
  };
}

function dedupeLegs(legs: ExtractedTransportLeg[]): ExtractedTransportLeg[] {
  const seen = new Set<string>();
  const out: ExtractedTransportLeg[] = [];
  for (const l of legs) {
    const k = legFingerprint(l);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(l);
  }
  return out;
}

export function legFingerprint(leg: ExtractedTransportLeg): string {
  const fn = (leg.flight_number || '').replace(/\s/g, '').toUpperCase();
  const o = normPlace(leg.origin);
  const d = normPlace(leg.destination);
  const day = leg.depart_at ? leg.depart_at.slice(0, 10) : '';
  return `flight|${o}|${d}|${day}|${fn}`;
}

export function routeRecordFingerprint(r: {
  mode: RouteMode;
  origin: string;
  destination: string;
  depart_at: string | null;
  flight_number?: string | null;
}): string {
  const fn = (r.flight_number || '').replace(/\s/g, '').toUpperCase();
  const o = normPlace(r.origin);
  const d = normPlace(r.destination);
  const day = r.depart_at ? r.depart_at.slice(0, 10) : '';
  return `${r.mode}|${o}|${d}|${day}|${fn}`;
}

function normPlace(s: string): string {
  const t = s.trim();
  const iata = t.match(/\b([A-Z]{3})\b/);
  if (iata) return iata[1];
  return t.toUpperCase().replace(/[^A-Z0-9ÄÖÅÆØ]/gi, '').slice(0, 24);
}

/**
 * Extract flight legs from boarding passes and travel-agent PDFs (Amex GBT style).
 */
export function extractTransportLegsFromDocument(text: string): ExtractedTransportLeg[] {
  if (!text?.trim()) return [];
  let legs = extractAgentStyleLegs(text);
  if (legs.length === 0) {
    const bp = extractBoardingPassLeg(text);
    if (bp) legs = [bp];
  }
  return dedupeLegs(legs);
}

/** Rough hotel / property line — used only to avoid duplicating lodging already saved as receipts */
export function extractHotelNameHints(text: string): string[] {
  const names: string[] = [];
  const t = text.replace(/\r/g, '\n');
  const re =
    /(?:Hotel|Accommodation|Property|Lodging)\s*(?:name)?\s*[:#]?\s*([^\n]{3,80})/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(t)) !== null) {
    const n = m[1].trim().replace(/\s{2,}/g, ' ');
    if (n.length > 2) names.push(n);
  }
  return names.filter((n, i) => names.indexOf(n) === i);
}

export function lodgingReceiptCoversHotel(hotelHint: string, receipts: Receipt[]): boolean {
  const h = hotelHint.toLowerCase().replace(/[^a-z0-9äöå\s]/gi, '').trim();
  if (h.length < 4) return false;
  const prefix = h.slice(0, Math.min(14, h.length));
  for (const r of receipts) {
    if (r.category !== 'lodging') continue;
    const blob = `${r.merchant} ${r.raw_text_excerpt || ''}`.toLowerCase();
    if (blob.includes(prefix)) return true;
    const words = prefix.split(/\s+/).filter((w) => w.length > 3);
    if (words.length && words.every((w) => blob.includes(w))) return true;
  }
  return false;
}
